import { Component } from "../types/ContentStructure";

type RGB = {
    r: number;
    g: number;
    b: number;
};
interface GradientColors {
    lightCss: string;
    darkCss: string;
}
/**
 * Convierte un color HEX a RGB
 */
export function hexToRgb(hex: string): RGB {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
        hex = hex.split("").map(c => c + c).join("");
    }
    const bigint = parseInt(hex, 16);
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255
    };
}
/**
 * Aclara un color RGB en un porcentaje
 */
function lightenColor(rgb: RGB, percent: number): RGB {
    return {
        r: Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * (percent / 100))),
        g: Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * (percent / 100))),
        b: Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * (percent / 100)))
    };
}

/**
 * Aclara un color manteniendo su tono base con ajuste de matiz
 */
export function lightenColorPreservingTone(rgb: RGB, percent: number): RGB {
    // Convertir RGB a HSL para mejor control del tono
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s, l = (max + min) / 2;
    
    if (max === min) {
        h = s = 0; // acromático
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
        }
    }
    
    // Ajuste especial para colores rojizos/rosados
    // Si está en el rango rojo-rosado (0-60° o 300-360°), ajustar ligeramente hacia magenta
    if ((h >= 0 && h <= 0.15) || (h >= 0.85 && h <= 1)) {
        h = h >= 0.5 ? h + 0.02 : h - 0.02; // Ajuste sutil hacia magenta
    }
    
    // Aumentar la luminosidad manteniendo tono y saturación ajustada
    l = Math.min(1, l + (1 - l) * (percent / 100));
    
    // Para colores muy saturados, mantener la saturación mejor
    if (s > 0.6) {
        s = s * (1 + (percent / 200)); // Aumentar saturación ligeramente
    }
    
    // Convertir de vuelta a RGB
    let rOut, gOut, bOut;
    
    if (s === 0) {
        rOut = gOut = bOut = l; // acromático
    } else {
        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
        };
        
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        
        rOut = hue2rgb(p, q, h + 1/3);
        gOut = hue2rgb(p, q, h);
        bOut = hue2rgb(p, q, h - 1/3);
    }
    
    return {
        r: Math.round(rOut * 255),
        g: Math.round(gOut * 255),
        b: Math.round(bOut * 255)
    };
}
/**
 * Convierte RGB a string CSS
 */
function rgbToCss(rgb: RGB): string {
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

/**
 * Genera un gradiente radial con un color base oscuro
 */
export function generateColors(hexColor: string, lightenPercent: number = 30): GradientColors {
    const dark = hexToRgb(hexColor);
    const light = lightenColorPreservingTone(dark, lightenPercent);

    const darkCss = rgbToCss(dark);
    const lightCss = rgbToCss(light);

    return { lightCss, darkCss };
}

export function generateLinearGradient(hexColor: string, lightenPercent: number = 30, angle: number = 130): string {
    const { darkCss, lightCss } = generateColors(hexColor, lightenPercent);
    return `linear-gradient(${angle}deg,${darkCss} 10%, ${lightCss} 30%, ${darkCss} 50%)`;
}
export function generateRadialGradient(hexColor: string, lightenPercent: number = 30): string {
    const { darkCss, lightCss } = generateColors(hexColor, lightenPercent);
    return `radial-gradient(circle at center, ${lightCss} 0%, ${darkCss} 80%)`;
}


export function getComponent(type: string, components: Component[]) {
    return components.filter((component: Component) => component.componentType.name === type);
}

export function slugify(text: string): string {
    return text
        .normalize("NFD")                          // Descompone caracteres Unicode (tildes, diéresis)
        .replace(/[\u0300-\u036f]/g, "")          // Elimina los acentos (rango unicode de diacríticos)
        .toLowerCase()                            // Convierte a minúsculas
        .trim()                                   // Elimina espacios al inicio y fin
        .replace(/\s+/g, "-")                     // Reemplaza espacios por guiones
        .replace(/[^\w\-]+/g, "")                 // Elimina todo lo que no sea letra, número o guion
        .replace(/\-\-+/g, "-");                  // Reemplaza múltiples guiones por uno solo
}
export function hexToBasicFilter(hex: string): string {
  // Elimina el "#" si está presente
  hex = hex.replace(/^#/, '');

  // Convierte a R, G, B
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  // Promedio para una aproximación de brillo
  const brightness = (r + g + b) / (3 * 255);

  // Inversión muy básica
  const invert = brightness > 0.5 ? 0.2 : 0.8;

  // Saturación simple basada en diferencia RGB
  const saturation = Math.max(r, g, b) - Math.min(r, g, b);
  const saturate = saturation > 100 ? 1.5 : 1.1;

  return `invert(${invert}) brightness(${brightness + 0.5}) saturate(${saturate})`;
}